import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { X } from "lucide-react";
import { localDayString } from "@/lib/local-day-range";

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  /** "Today" and the 7d/30d ranges are the viewer's days, not UTC days. */
  localDays?: boolean;
}

export const DateRangePicker = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  localDays = false,
}: DateRangePickerProps) => {
  const dayOf = (moment: Date) =>
    localDays ? localDayString(moment) : moment.toISOString().slice(0, 10);
  const today = dayOf(new Date());

  const handleQuickSelect = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    onStartDateChange(dayOf(start));
    onEndDateChange(dayOf(end));
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2">
        <div className="relative flex items-center">
          <Input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            max={endDate || today}
            className="h-8 w-[130px] pr-7 text-sm"
            placeholder="Start date"
          />
          {startDate && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0.5 size-6 rounded-full hover:bg-muted"
              onClick={() => onStartDateChange("")}
              title="Clear"
            >
              <X className="size-3" />
            </Button>
          )}
        </div>
        <span className="text-xs text-muted-foreground">–</span>
        <div className="relative flex items-center">
          <Input
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            min={startDate}
            max={today}
            className="h-8 w-[130px] pr-7 text-sm"
            placeholder="End date"
          />
          {endDate && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0.5 size-6 rounded-full hover:bg-muted"
              onClick={() => onEndDateChange("")}
              title="Clear"
            >
              <X className="size-3" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={() => handleQuickSelect(7)}
        >
          7d
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={() => handleQuickSelect(30)}
        >
          30d
        </Button>
      </div>
    </div>
  );
};
